/**
 * «معمولاً کِی آزادی؟» — قرینه‌ی apps/mobile/app/availability.tsx.
 *
 * همان جدولِ ساعت×روز و همان سه میان‌بر: کل یک ساعت (همه‌ی روزها)، کل یک
 * روز، و آخرِ هفته. داده از همان `availabilityRepo` می‌آید که پیشنهادِ
 * «وقتِ مشترک» در فرمِ ساختِ سیگنال هم از رویش می‌خواند.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { availabilityRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { days, t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Btn, Card, Help, Note } from '@/ui/kit';

const H0 = 8;
const H1 = 24;
const HOURS = Array.from({ length: H1 - H0 }, (_, i) => H0 + i);
const keyOf = (d: number, h: number) => `${d}-${h}`;

export function AvailabilityPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [on, setOn] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);

  const slots = useQuery({
    queryKey: ['availability'],
    queryFn: () => availabilityRepo.mine(profile!.id),
    enabled: !!profile,
  });

  useEffect(() => {
    if (slots.data) {
      setOn(new Set(slots.data.filter((c) => c.state === 'free').map((c) => keyOf(c.dow, c.hour))));
    }
  }, [slots.data]);

  const setCell = (d: number, h: number, v: boolean) => {
    const k = keyOf(d, h);
    setOn((prev) => {
      const next = new Set(prev);
      if (v) next.add(k); else next.delete(k);
      return next;
    });
    setDirty((prev) => new Map(prev).set(k, v));
  };
  const toggleCell = (d: number, h: number) => setCell(d, h, !on.has(keyOf(d, h)));

  const DOW = days();
  const rowAll = (h: number) => {
    const everyOn = DOW.every((_x, d) => on.has(keyOf(d, h)));
    DOW.forEach((_x, d) => setCell(d, h, !everyOn));
  };
  const weekendAll = (h: number) => {
    const bothOn = on.has(keyOf(0, h)) && on.has(keyOf(1, h));
    setCell(0, h, !bothOn); setCell(1, h, !bothOn);
  };
  const dayAll = (d: number) => {
    const everyOn = HOURS.every((h) => on.has(keyOf(d, h)));
    HOURS.forEach((h) => setCell(d, h, !everyOn));
  };

  const save = async () => {
    if (!profile || dirty.size === 0) { nav('/profile'); return; }
    setSaving(true);
    try {
      const adds: { dow: number; hour: number }[] = [];
      const dels: { dow: number; hour: number }[] = [];
      dirty.forEach((v, k) => {
        const [d, h] = k.split('-').map(Number);
        (v ? adds : dels).push({ dow: d, hour: h });
      });
      await availabilityRepo.setCells(profile.id, adds, true);
      await availabilityRepo.setCells(profile.id, dels, false);
      qc.invalidateQueries({ queryKey: ['availability'] });
      nav('/profile');
    } catch (e) {
      reportError('web.availability.save', e);
      setSaving(false);
    }
  };

  return (
    <div className="feed">
      {/* «این به چه دردی می‌خورد» پشتِ «؟» کنارِ عنوان، نه کارتی تهِ صفحه. */}
      <h1 className="feed-title">{t('availability')}<Help text={t('availabilityExplain')} /></h1>
      <Note>{t('availabilityHint')} · {t('tapDayNameHint')}</Note>

      <Card>
        <div className="avail-grid" style={{ gridTemplateColumns: `62px repeat(${HOURS.length}, 1fr)` }}>
          <span />
          {HOURS.map((h) => <span key={h} className="avail-hour">{h % 4 === 0 ? h : ''}</span>)}

          <button type="button" className="avail-row-label brand" onClick={() => rowAll(H0)}>
            {t('allDays')}
          </button>
          {HOURS.map((h) => (
            <button key={h} type="button"
              className={`avail-cell special${DOW.every((_x, d) => on.has(keyOf(d, h))) ? ' on' : ''}`}
              onClick={() => rowAll(h)} aria-label={`${t('allDays')} ${h}`} />
          ))}

          {DOW.map((day, d) => (
            <React.Fragment key={day}>
              <button type="button" className="avail-row-label" onClick={() => dayAll(d)}>{day}</button>
              {HOURS.map((h) => (
                <button key={h} type="button" className={`avail-cell${on.has(keyOf(d, h)) ? ' on' : ''}`}
                  onClick={() => toggleCell(d, h)} aria-label={`${day} ${h}`} />
              ))}
            </React.Fragment>
          ))}

          <button type="button" className="avail-row-label brand" onClick={() => weekendAll(0)}>
            {t('weekend')}
          </button>
          {HOURS.map((h) => (
            <button key={h} type="button"
              className={`avail-cell special${on.has(keyOf(0, h)) && on.has(keyOf(1, h)) ? ' on' : ''}`}
              onClick={() => weekendAll(h)} aria-label={`${t('weekend')} ${h}`} />
          ))}
        </div>
      </Card>

      <Btn label={saving ? '…' : t('saveAndBack')} disabled={saving} onClick={save} />
    </div>
  );
}
